import React, { useEffect, useState } from 'react';
import { Tab, Tabs, Typography, List, CircularProgress } from '@mui/material';
import { Box, Container } from '@mui/system';
import { FlightReservation, RootState, UserState } from '~/types';
import { useSelector, useDispatch } from 'react-redux';
import ManageTrip from '../components/ManageTrip';
import { STATUS } from '~/literals';
import StandardPage from '~/layouts/StandardPage';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getUserReservations, resetBookingData } from '~/actions';
import { useNavigate } from 'react-router-dom';


const ManageTripsPage = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const status = useSelector<RootState, string>(({ flightBooking }) => flightBooking.pageStatus);
  const flightReservations = useSelector<RootState, FlightReservation[]>(({ flightBooking }) => flightBooking.userReservations);
  const errorMessage = useSelector<RootState, string | undefined>(({ flightBooking }) => flightBooking?.errorMessage);
  const userState = useSelector<RootState, UserState | null>(({ user }) => user);

  const today = new Date();

  useEffect(() => {
    dispatch(resetBookingData());
    dispatch(getUserReservations());
  }, []);

  useEffect(() => {
    if (status === STATUS.LOGIN_REQUIRED && !userState?.isAuthenticated) {
      navigate('/login', { state: { from: 'manage' } });
    }
  }, [status, userState]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    setTabIndex(newIndex);
  };

  const handleViewButtonClicked = (id: string) => {
    navigate(`/manage/${id}`, { state: { from: 'manage' } });
  };

  const upcomingTrips = flightReservations.filter((reservation: FlightReservation) => {
    if (reservation.bookingDetails.offers) {
      const departureDate = new Date(reservation.bookingDetails.offers[0].itineraries.outbound.legs[0].departure.dateTime);
      return departureDate >= today;
    }
    return false;
  });

  const pastTrips = flightReservations.filter((reservation: FlightReservation) => {
    if (reservation.bookingDetails.offers) {
      const departureDate = new Date(reservation.bookingDetails.offers[0].itineraries.outbound.legs[0].departure.dateTime);
      return departureDate < today;
    }
    return false;
  });

  let reservations;
  let displayError;

  if (status === STATUS.SUCCESS) {
    reservations = (
      <Box>
        <Typography variant="h4" gutterBottom>
          My Trips
        </Typography>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="Upcoming Trips" />
          <Tab label="Past Trips" />
        </Tabs>

        {
          tabIndex === 0 && (
            <>
              {!upcomingTrips.length && <Typography variant='body1'>No upcoming trips. It's time to use Tripenger and have something to look forward to!</Typography>}
              <List>
                {upcomingTrips.map((reservation: FlightReservation) => (
                  <ManageTrip
                    onViewButtonClicked={handleViewButtonClicked}
                    key={reservation.reservationId}
                    reservation={reservation} />
                ))}
              </List>
            </>
          )
        }

        {
          tabIndex === 1 && (
            <>
              {!pastTrips.length && <Typography variant='body1'>No past trips.</Typography>}
              <List>
                {pastTrips.map((reservation: FlightReservation) => (
                  <ManageTrip
                    onViewButtonClicked={handleViewButtonClicked}
                    key={reservation.reservationId}
                    reservation={reservation} />
                ))}
              </List>
            </>
          )
        }
      </Box>
    );
  } else if (errorMessage && status === STATUS.ERROR) {
    displayError = (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ p: 2 }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong.
        </Typography>
        <Typography variant="body1" gutterBottom>
          {errorMessage}
        </Typography>
      </Box>
    )
  }

  return (
    <StandardPage
      bannerContent={
        <Box textAlign="left">
          <Typography variant="h3" gutterBottom sx={{ color: '#fff', textAlign: 'left' }}>
            Manage Your Trips
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#fff', textAlign: 'left' }}>
            View, review, and relive your journeys. All your reservations in one place.
          </Typography>
        </Box>
      }
    >
      {
        status === STATUS.RUNNING &&
        <Container sx={{ textAlign: 'center' }}>
          <Typography variant='body1'>Please wait</Typography>
          <CircularProgress />
        </Container>
      }
      {displayError}
      {reservations}
    </StandardPage>
  );
};

export default ManageTripsPage;
